import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Plus, Eye, Edit2, Trash2, Layout, Save, BarChart2, TrendingUp, Table, Hash, CheckCircle, AlertCircle, Settings, Copy } from 'lucide-react';
import QoCService from '../../services/QoCService';
import FacilityService from '../../services/FacilityService';

// Data sources from your QoC system
const dataSources = [
  { id: 'current_compliance', name: 'Current Compliance', category: 'Overview', description: 'Overall compliance percentage and key metrics', fields: ['overall_compliance', 'overdue_count', 'action_items_count'], filters: ['facility_id', 'region', 'level'] },
  { id: 'compliance_trend', name: 'Compliance Trends', category: 'Trends', description: 'Historical compliance data over time', fields: ['month', 'avg_compliance', 'assessment_count'], filters: ['facility_id', 'region', 'months'] },
  { id: 'comparison_report', name: 'Facility Comparison', category: 'Comparison', description: 'Compare facilities by performance', fields: ['facility_name', 'average_percentage', 'assessment_count'], filters: ['region', 'level', 'from_date', 'to_date'] },
  { id: 'standards_performance', name: 'Standards Performance', category: 'Standards', description: 'Performance by standard category', fields: ['standard_name', 'average_percentage', 'facility_count'], filters: ['region', 'level'] },
  { id: 'overdue_assessments', name: 'Overdue Assessments', category: 'Risk', description: 'Facilities with overdue assessments', fields: ['facility_name', 'days_overdue', 'priority'], filters: ['facility_id', 'region'] },
  { id: 'performance_distribution', name: 'Performance Distribution', category: 'Distribution', description: 'Facilities grouped by score ranges', fields: ['range_label', 'facility_count'], filters: ['region', 'level'] },
  { id: 'quick_wins', name: 'Quick Wins', category: 'Opportunities', description: 'Facilities close to next tier', fields: ['facility_name', 'points_needed', 'next_tier'], filters: ['region', 'level'] }
];

const visualizationTypes = [
  { value: 'line', label: 'Line Chart', icon: TrendingUp, bestFor: 'trends' },
  { value: 'bar', label: 'Bar Chart', icon: BarChart2, bestFor: 'comparison' },
  { value: 'pie', label: 'Pie Chart', icon: TrendingUp, bestFor: 'distribution' },
  { value: 'stat', label: 'Statistic Card', icon: Hash, bestFor: 'single value' },
  { value: 'table', label: 'Data Table', icon: Table, bestFor: 'detailed data' }
];

const colorSchemes = [
  { value: 'default', label: 'Default', colors: ['#7c3aed', '#06b6d4', '#10b981', '#f59e0b', '#ef4444'] },
  { value: 'performance', label: 'Performance', colors: ['#f5222d', '#fa8c16', '#faad14', '#73d13d', '#52c41a'] },
  { value: 'professional', label: 'Professional', colors: ['#001529', '#1890ff', '#52c41a', '#faad14', '#f5222d'] }
];

export default function DashboardBuilder() {
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDashboardModal, setShowDashboardModal] = useState(false);
  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [dashboardWidgets, setDashboardWidgets] = useState([]);
  const [editingWidget, setEditingWidget] = useState(null);
  const [activeTab, setActiveTab] = useState('data');
  
  // Dashboard form
  const [dashboardName, setDashboardName] = useState('');
  const [dashboardDesc, setDashboardDesc] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  
  // Widget form
  const [widgetTitle, setWidgetTitle] = useState('');
  const [selectedDataSource, setSelectedDataSource] = useState('');
  const [selectedVizType, setSelectedVizType] = useState('');
  const [widgetSize, setWidgetSize] = useState('medium');
  const [colorScheme, setColorScheme] = useState('default');
  const [widgetFilters, setWidgetFilters] = useState({});
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    loadDashboards();
  }, []);

  const loadDashboards = async () => {
    setLoading(true);
    try {
      const response = await QoCService.getDashboards(false);
      setDashboards(response.data?.data || []);
    } catch (error) {
      console.error('Error loading dashboards:', error);
      alert('Failed to load dashboards');
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardWidgets = async (dashboardId) => {
    try {
      const response = await QoCService.getDashboardDetail(dashboardId, false);
      setDashboardWidgets(response.data?.data?.widgets || []);
    } catch (error) {
      console.error('Error loading widgets:', error);
    }
  };

  const handleCreateDashboard = () => {
    setDashboardName('');
    setDashboardDesc('');
    setIsDefault(false);
    setSelectedDashboard(null);
    setShowDashboardModal(true);
  };

  const handleSaveDashboard = async () => {
    if (!dashboardName.trim()) {
      alert('Please enter dashboard name');
      return;
    }
    
    setLoading(true);
    try {
      const data = {
        name: dashboardName,
        description: dashboardDesc,
        is_default: isDefault ? 1 : 0
      };
      
      if (selectedDashboard) {
        data.id = selectedDashboard.id;
      }
      
      await QoCService.saveDashboard(data);
      alert(selectedDashboard ? 'Dashboard updated successfully' : 'Dashboard created successfully');
      setShowDashboardModal(false);
      loadDashboards();
    } catch (error) {
      console.error('Error saving dashboard:', error);
      alert('Failed to save dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDashboard = async (id) => {
    if (!window.confirm('Delete this dashboard?')) return;
    
    setLoading(true);
    try {
      await QoCService.deleteDashboard(id);
      alert('Dashboard deleted successfully');
      loadDashboards();
    } catch (error) {
      console.error('Error deleting dashboard:', error);
      alert('Failed to delete dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateDashboard = async (id, name) => {
    const newName = prompt('Enter name for duplicated dashboard:', name + ' (Copy)');
    if (!newName) return;
    
    setLoading(true);
    try {
      await QoCService.duplicateDashboard(id, newName);
      alert('Dashboard duplicated successfully');
      loadDashboards();
    } catch (error) {
      console.error('Error duplicating dashboard:', error);
      alert('Failed to duplicate dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleAddWidget = (dashboard) => {
    setSelectedDashboard(dashboard);
    setWidgetTitle('');
    setSelectedDataSource('');
    setSelectedVizType('');
    setWidgetSize('medium');
    setColorScheme('default');
    setWidgetFilters({});
    setPreviewData(null);
    setEditingWidget(null);
    setActiveTab('data');
    loadDashboardWidgets(dashboard.id);
    setShowWidgetModal(true);
  };

  const handleSaveWidget = async () => {
    if (!widgetTitle.trim() || !selectedDataSource || !selectedVizType) {
      alert('Please fill all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const data = {
        dashboard_id: selectedDashboard.id,
        title: widgetTitle,
        data_source: selectedDataSource,
        viz_type: selectedVizType,
        filters: widgetFilters,
        size: widgetSize,
        color_scheme: colorScheme,
        config: { colorScheme },
        display_order: dashboardWidgets.length
      };
      
      if (editingWidget) {
        data.id = editingWidget.id;
      }
      
      await QoCService.saveWidget(data);
      alert(editingWidget ? 'Widget updated successfully' : 'Widget added successfully');
      setShowWidgetModal(false);
      loadDashboards();
    } catch (error) {
      console.error('Error saving widget:', error);
      alert('Failed to save widget');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWidget = async (widgetId) => {
    if (!window.confirm('Delete this widget?')) return;
    
    try {
      await QoCService.deleteWidget(widgetId);
      alert('Widget deleted successfully');
      loadDashboardWidgets(selectedDashboard.id);
    } catch (error) {
      console.error('Error deleting widget:', error);
      alert('Failed to delete widget');
    }
  };

  const fetchPreviewData = async () => {
    if (!selectedDataSource || !selectedVizType) {
      alert('Please select data source and visualization type');
      return;
    }
    
    setPreviewLoading(true);
    try {
      let response;
      const filters = widgetFilters;
      
      switch (selectedDataSource) {
        case 'current_compliance':
          response = await QoCService.getCurrentCompliance(filters.facility_id);
          break;
        case 'compliance_trend':
          response = await QoCService.getComplianceTrends(filters.facility_id, filters.months || 12);
          break;
        case 'comparison_report':
          response = await QoCService.getComparisonReport(filters);
          break;
        case 'standards_performance':
          response = await QoCService.getAggregatedStandardsPerformance(filters);
          break;
        case 'overdue_assessments':
          response = await QoCService.getOverdueAssessments(filters.facility_id);
          break;
        case 'performance_distribution':
          response = await QoCService.getPerformanceDistribution(filters);
          break;
        case 'quick_wins':
          response = await QoCService.getQuickWins(filters);
          break;
        default:
          response = { data: { data: [] } };
      }
      
      const rawData = response.data?.data;
      const formattedData = formatDataForVisualization(rawData, selectedDataSource, selectedVizType);
      setPreviewData({ data: formattedData, vizType: selectedVizType, dataSource: selectedDataSource });
    } catch (error) {
      console.error('Error fetching preview data:', error);
      alert('Failed to load preview data');
    } finally {
      setPreviewLoading(false);
    }
  };

  const formatDataForVisualization = (data, source, vizType) => {
    if (!data) return [];
    
    switch (source) {
      case 'compliance_trend':
        return Array.isArray(data) ? data.map(d => ({
          month: d.month,
          value: parseFloat(d.avg_compliance || 0)
        })) : [];
        
      case 'comparison_report':
        return Array.isArray(data) ? data.slice(0, 10).map(d => ({
          name: d.facility_name,
          score: parseFloat(d.average_percentage || 0)
        })) : [];
        
      case 'performance_distribution':
        return Array.isArray(data) ? data.map(d => ({
          name: d.range_label,
          value: parseInt(d.facility_count || 0)
        })) : [];
        
      case 'current_compliance':
        return { value: parseFloat(data.overall_compliance || 0) };
        
      case 'standards_performance':
        return Array.isArray(data) ? data.slice(0, 6).map(d => ({
          name: d.standard_name,
          value: parseFloat(d.average_percentage || 0)
        })) : [];
        
      case 'overdue_assessments':
        return Array.isArray(data) ? data.slice(0, 10).map(d => ({
          name: d.facility_name,
          days: parseInt(d.days_overdue || 0)
        })) : [];
        
      case 'quick_wins':
        return Array.isArray(data) ? data.map(d => ({
          name: d.facility_name,
          points: parseFloat(d.points_needed || 0),
          tier: d.next_tier
        })) : [];
        
      default:
        return [];
    }
  };

  const renderPreview = () => {
    if (previewLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
          <div>Loading preview...</div>
        </div>
      );
    }
    
    if (!previewData) {
      return (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
          <Settings size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <div>Configure widget and click "Generate Preview"</div>
        </div>
      );
    }

    const { data, vizType } = previewData;
    const colors = colorSchemes.find(cs => cs.value === colorScheme)?.colors || colorSchemes[0].colors;

    if (vizType === 'line') {
      return (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="value" stroke={colors[0]} strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (vizType === 'bar') {
      return (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="score" fill={colors[0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (vizType === 'pie') {
      return (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    if (vizType === 'stat') {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '64px', fontWeight: 'bold', color: colors[0] }}>
            {data.value ? data.value.toFixed(1) : '0'}%
          </div>
          <div style={{ fontSize: '18px', color: '#666', marginTop: '12px' }}>
            Overall Compliance
          </div>
        </div>
      );
    }

    if (vizType === 'table') {
      return (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                {Object.keys(data[0] || {}).map(key => (
                  <th key={key} style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>
                    {key.replace(/_/g, ' ').toUpperCase()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.slice(0, 5).map((row, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  {Object.values(row).map((val, j) => (
                    <td key={j} style={{ padding: '8px' }}>{val}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Preview not available</div>;
  };

  const Modal = React.memo(({ show, onClose, title, children, onSave, width = '600px' }) => {
    if (!show) return null;
    
    const handleBackdropClick = (e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    };
    
    return (
      <div 
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
        onClick={handleBackdropClick}
      >
        <div style={{ backgroundColor: 'white', borderRadius: '8px', width, maxWidth: '95%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>{title}</h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', padding: '0 8px', lineHeight: 1 }}>×</button>
          </div>
          <div style={{ padding: '24px' }}>{children}</div>
          {onSave && (
            <div style={{ padding: '16px 24px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={onClose} style={{ padding: '8px 16px', border: '1px solid #d9d9d9', borderRadius: '4px', background: 'white', cursor: 'pointer' }}>Cancel</button>
              <button onClick={onSave} style={{ padding: '8px 16px', border: 'none', borderRadius: '4px', background: '#1890ff', color: 'white', cursor: 'pointer', fontWeight: 500 }}>Save</button>
            </div>
          )}
        </div>
      </div>
    );
  });

  const Button = ({ onClick, children, variant = 'default', icon, size = 'md', disabled = false }) => {
    const styles = {
      default: { background: 'white', border: '1px solid #d9d9d9', color: '#000' },
      primary: { background: '#1890ff', border: 'none', color: 'white' },
      danger: { background: '#ff4d4f', border: 'none', color: 'white' }
    };
    const sizeStyles = {
      sm: { padding: '4px 12px', fontSize: '13px' },
      md: { padding: '8px 16px', fontSize: '14px' }
    };
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        style={{
          ...styles[variant],
          ...sizeStyles[size],
          borderRadius: '4px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          fontWeight: 500,
          opacity: disabled ? 0.6 : 1
        }}
      >
        {icon}
        {children}
      </button>
    );
  };

  return (
    <div className="page-content">
      <div className="page-head uc d-flex">
        <div className="my-auto">
          <h2>Dashboard Builder</h2>
        </div>
        <div className="ml-auto">
          <Button onClick={handleCreateDashboard} variant="primary" icon={<Plus size={16} />}>
            Create Dashboard
          </Button>
        </div>
      </div>

      <div className="page-pad">
        <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ padding: '16px 24px', background: '#e6f7ff', borderBottom: '1px solid #91d5ff' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <CheckCircle size={18} style={{ color: '#1890ff', marginTop: '2px', flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>Custom Dashboard Builder</div>
                <div style={{ fontSize: '13px', color: '#595959' }}>
                  Create personalized dashboards by selecting data sources and visualization types. No coding required!
                </div>
              </div>
            </div>
          </div>

          <div style={{ padding: '24px' }}>
            {loading && <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>}
            {!loading && dashboards.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <Layout size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
                <div>No dashboards yet. Create your first dashboard!</div>
              </div>
            )}
            {!loading && dashboards.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, color: '#595959' }}>Dashboard Name</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, color: '#595959' }}>Description</th>
                    <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, color: '#595959' }}>Widgets</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, color: '#595959' }}>Created</th>
                    <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, color: '#595959', width: '300px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboards.map(dashboard => (
                    <tr key={dashboard.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px 8px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span style={{ fontWeight: 500 }}>{dashboard.name}</span>
                          {dashboard.is_default == 1 && (
                            <span style={{ padding: '2px 8px', background: '#e6f7ff', color: '#1890ff', borderRadius: '4px', fontSize: '12px' }}>Default</span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px 8px', color: '#595959' }}>{dashboard.description}</td>
                      <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                        <span style={{ padding: '2px 8px', background: '#f0f0f0', borderRadius: '4px', fontSize: '13px' }}>{dashboard.widget_count}</span>
                      </td>
                      <td style={{ padding: '12px 8px', color: '#595959' }}>{dashboard.created_at}</td>
                      <td style={{ padding: '12px 8px' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          <Button size="sm" variant="primary" icon={<Layout size={14} />} onClick={() => handleAddWidget(dashboard)}>Widgets</Button>
                          <Button size="sm" icon={<Edit2 size={14} />} onClick={() => {
                            setSelectedDashboard(dashboard);
                            setDashboardName(dashboard.name);
                            setDashboardDesc(dashboard.description);
                            setIsDefault(dashboard.is_default == 1);
                            setShowDashboardModal(true);
                          }} />
                          <Button size="sm" icon={<Copy size={14} />} onClick={() => handleDuplicateDashboard(dashboard.id, dashboard.name)} />
                          <Button size="sm" variant="danger" icon={<Trash2 size={14} />} onClick={() => handleDeleteDashboard(dashboard.id)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Dashboard Modal */}
      <Modal
        show={showDashboardModal}
        onClose={() => setShowDashboardModal(false)}
        onSave={handleSaveDashboard}
        title={selectedDashboard ? 'Edit Dashboard' : 'Create Dashboard'}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>Dashboard Name *</label>
            <input
              type="text"
              value={dashboardName}
              onChange={(e) => setDashboardName(e.target.value)}
              placeholder="e.g., Regional Performance Dashboard"
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '14px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>Description</label>
            <textarea
              value={dashboardDesc}
              onChange={(e) => setDashboardDesc(e.target.value)}
              placeholder="Brief description of this dashboard"
              rows={3}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '14px', resize: 'vertical' }}
            />
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                style={{ width: '16px', height: '16px' }}
              />
              <span>Set as default dashboard</span>
            </label>
          </div>
        </div>
      </Modal>

      {/* Widget Modal */}
      <Modal
        show={showWidgetModal}
        onClose={() => setShowWidgetModal(false)}
        onSave={handleSaveWidget}
        title={editingWidget ? 'Edit Widget' : 'Add Widget'}
        width="900px"
      >
        {/* Widget list for current dashboard */}
        {dashboardWidgets.length > 0 && (
          <div style={{ marginBottom: '24px', padding: '16px', background: '#fafafa', borderRadius: '8px' }}>
            <div style={{ fontWeight: 600, marginBottom: '12px' }}>Current Widgets ({dashboardWidgets.length})</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {dashboardWidgets.map(widget => (
                <div key={widget.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', background: 'white', borderRadius: '4px' }}>
                  <div>
                    <div style={{ fontWeight: 500 }}>{widget.title}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{widget.data_source} • {widget.viz_type}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button size="sm" icon={<Edit2 size={12} />} onClick={() => {
                      setEditingWidget(widget);
                      setWidgetTitle(widget.title);
                      setSelectedDataSource(widget.data_source);
                      setSelectedVizType(widget.viz_type);
                      setWidgetSize(widget.size);
                      setColorScheme(widget.color_scheme);
                      setWidgetFilters(widget.filters || {});
                    }} />
                    <Button size="sm" variant="danger" icon={<Trash2 size={12} />} onClick={() => handleDeleteWidget(widget.id)} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', gap: '4px' }}>
            {['data', 'filters', 'styling', 'preview'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '10px 20px',
                  background: 'none',
                  border: 'none',
                  borderBottom: activeTab === tab ? '2px solid #1890ff' : '2px solid transparent',
                  color: activeTab === tab ? '#1890ff' : '#595959',
                  fontWeight: activeTab === tab ? 600 : 400,
                  cursor: 'pointer',
                  textTransform: 'capitalize'
                }}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'data' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>Widget Title *</label>
              <input
                type="text"
                value={widgetTitle}
                onChange={(e) => setWidgetTitle(e.target.value)}
                placeholder="e.g., Monthly Compliance Trend"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>Data Source *</label>
                <select
                  value={selectedDataSource}
                  onChange={(e) => setSelectedDataSource(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
                >
                  <option value="">Select data source</option>
                  {Object.entries(
                    dataSources.reduce((acc, ds) => {
                      if (!acc[ds.category]) acc[ds.category] = [];
                      acc[ds.category].push(ds);
                      return acc;
                    }, {})
                  ).map(([category, sources]) => (
                    <optgroup key={category} label={category}>
                      {sources.map(ds => (
                        <option key={ds.id} value={ds.id}>{ds.name}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {selectedDataSource && (
                  <div style={{ marginTop: '8px', padding: '8px 12px', background: '#e6f7ff', borderRadius: '4px', fontSize: '13px', color: '#595959' }}>
                    {dataSources.find(d => d.id === selectedDataSource)?.description}
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>Visualization Type *</label>
                <select
                  value={selectedVizType}
                  onChange={(e) => setSelectedVizType(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
                >
                  <option value="">Select chart type</option>
                  {visualizationTypes.map(vt => (
                    <option key={vt.value} value={vt.value}>{vt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'filters' && (
          <div>
            {selectedDataSource ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {dataSources.find(d => d.id === selectedDataSource)?.filters.map(filter => (
                  <div key={filter}>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, textTransform: 'capitalize' }}>
                      {filter.replace(/_/g, ' ')}
                    </label>
                    <input
                      type={filter.includes('date') ? 'date' : filter === 'months' ? 'number' : 'text'}
                      value={widgetFilters[filter] || ''}
                      onChange={(e) => setWidgetFilters({ ...widgetFilters, [filter]: e.target.value })}
                      placeholder={`Filter by ${filter}`}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                Select a data source first
              </div>
            )}
          </div>
        )}

        {activeTab === 'styling' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>Color Scheme</label>
              <select
                value={colorScheme}
                onChange={(e) => setColorScheme(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
              >
                {colorSchemes.map(cs => (
                  <option key={cs.value} value={cs.value}>{cs.label}</option>
                ))}
              </select>
              <div style={{ marginTop: '8px', display: 'flex', gap: '4px' }}>
                {colorSchemes.find(cs => cs.value === colorScheme)?.colors.map((color, i) => (
                  <div
                    key={i}
                    style={{
                      width: '32px',
                      height: '32px',
                      backgroundColor: color,
                      borderRadius: '4px',
                      border: '1px solid #e0e0e0'
                    }}
                  />
                ))}
              </div>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>Widget Size</label>
              <select
                value={widgetSize}
                onChange={(e) => setWidgetSize(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
              >
                <option value="small">Small (1/4 width)</option>
                <option value="medium">Medium (1/2 width)</option>
                <option value="large">Large (Full width)</option>
              </select>
            </div>
          </div>
        )}

        {activeTab === 'preview' && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <Button
                onClick={fetchPreviewData}
                variant="primary"
                disabled={!selectedDataSource || !selectedVizType || previewLoading}
              >
                {previewLoading ? 'Loading...' : 'Generate Preview'}
              </Button>
            </div>
            {renderPreview()}
          </div>
        )}
      </Modal>
    </div>
  );
}